import React, { useState, useRef } from "react";
import {
	AppBar,
	Toolbar,
	IconButton,
	Typography,
	Menu,
	MenuItem,
	ListItemIcon,
	ListItemText,
	Fab,
	Divider,
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Alert,
} from "@mui/material";
import {
	Add as AddIcon,
	MoreVert as MoreIcon,
	Delete as DeleteIcon,
	Download as DownloadIcon,
	Upload as UploadIcon,
	Publish as PublishIcon,
	Map as MapIcon,
	SelectAll as SelectAllIcon,
	Warning as WarningIcon,
	ContentCopy as DuplicateIcon,
	Merge as MergeIcon,
	CallSplit as SplitIcon,
} from "@mui/icons-material";
import type { CSVImportResult, CSVImportSummary } from "../ImportTable.csv";

/**
 * Props for the mobile toolbar component
 */
export interface ImportTableToolbarMobileProps {
	jobCount: number;
	selectedCount: number;
	hasValidationErrors: boolean;
	onAddJob: () => void;
	onDeleteSelected: () => void;
	onDuplicateSelected: () => void;
	onMergeSelected: () => void;
	onSplitSelected?: () => void;
	onSelectAll: () => void;
	onCreateJobs: () => Promise<void>;
	onExportCSV: () => void;
	onImportCSV: (file: File) => Promise<void>;
	csvImportPending: { result: CSVImportResult; summary: CSVImportSummary } | null;
	onConfirmCSVImport: () => void;
	onCancelCSVImport: () => void;
	csvImportErrors: string[] | null;
	onDismissCsvImportErrors: () => void;
	mapDisplayMode?: "modal" | "drawer" | null;
	setMapDisplayMode?: (mode: "modal" | "drawer" | null) => void;
	/** For split feature - IDs of features selected via lasso on map */
	selectedFeatureIds?: Set<string>;
	/** For split feature - ID of the single selected job */
	selectedJobId?: string;
}

/**
 * Mobile toolbar for ImportTable.
 * Features:
 * - Sticky AppBar at top
 * - Overflow menu for less-frequent actions
 * - FAB for adding new jobs
 * - Selection mode indicator
 */
export const ImportTableToolbarMobile: React.FC<ImportTableToolbarMobileProps> = ({
	jobCount,
	selectedCount,
	hasValidationErrors,
	onAddJob,
	onDeleteSelected,
	onDuplicateSelected,
	onMergeSelected,
	onSplitSelected,
	onSelectAll,
	onCreateJobs,
	onExportCSV,
	onImportCSV,
	csvImportPending,
	onConfirmCSVImport,
	onCancelCSVImport,
	csvImportErrors,
	onDismissCsvImportErrors,
	setMapDisplayMode,
	selectedFeatureIds,
}) => {
	const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const menuOpen = Boolean(menuAnchor);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
		setMenuAnchor(event.currentTarget);
	};

	const handleMenuClose = () => {
		setMenuAnchor(null);
	};

	const handleCreateJobs = async () => {
		handleMenuClose();
		setIsSubmitting(true);
		try {
			await onCreateJobs();
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleExportCSV = () => {
		handleMenuClose();
		onExportCSV();
	};

	const handleUploadCSV = () => {
		handleMenuClose();
		fileInputRef.current?.click();
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			onImportCSV(file);
			e.target.value = "";
		}
	};

	const handleShowMap = () => {
		handleMenuClose();
		setMapDisplayMode?.("modal");
	};

	const handleDeleteSelected = () => {
		handleMenuClose();
		onDeleteSelected();
	};

	const handleDuplicateSelected = () => {
		handleMenuClose();
		onDuplicateSelected();
	};

	const handleMergeSelected = () => {
		handleMenuClose();
		onMergeSelected();
	};

	const handleSplitSelected = () => {
		handleMenuClose();
		onSplitSelected?.();
	};

	const isSelectionMode = selectedCount > 0;
	const canMerge = selectedCount >= 2;
	const canSplit = selectedCount === 1 && selectedFeatureIds && selectedFeatureIds.size > 0;

	return (
		<>
			<AppBar position="sticky" color={isSelectionMode ? "primary" : "default"} elevation={1} sx={{ top: 0 }}>
				<Toolbar>
					{/* Title / Selection Count */}
					<Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
						{isSelectionMode ? `${selectedCount} selected` : `Jobs (${jobCount})`}
					</Typography>

					{/* Submit Button with validation indicator */}
					<Button
						variant={isSelectionMode ? "outlined" : "contained"}
						color={hasValidationErrors ? "warning" : "primary"}
						onClick={handleCreateJobs}
						disabled={isSubmitting || jobCount === 0}
						startIcon={hasValidationErrors ? <WarningIcon /> : <PublishIcon />}
						sx={{
							ml: 1,
							minWidth: "auto",
							px: 2,
							...(isSelectionMode && { color: "inherit", borderColor: "inherit" }),
						}}
					>
						{isSubmitting ? "..." : "Submit"}
					</Button>

					{/* Overflow Menu */}
					<IconButton color="inherit" onClick={handleMenuOpen} aria-label="more options" sx={{ ml: 1 }}>
						<MoreIcon />
					</IconButton>
				</Toolbar>
			</AppBar>

			{/* Overflow Menu */}
			<Menu
				anchorEl={menuAnchor}
				open={menuOpen}
				onClose={handleMenuClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
			>
				<MenuItem onClick={handleExportCSV}>
					<ListItemIcon>
						<DownloadIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText>Download CSV</ListItemText>
				</MenuItem>
				<MenuItem onClick={handleUploadCSV}>
					<ListItemIcon>
						<UploadIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText>Upload CSV</ListItemText>
				</MenuItem>
				{setMapDisplayMode && (
					<MenuItem onClick={handleShowMap}>
						<ListItemIcon>
							<MapIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText>View Map</ListItemText>
					</MenuItem>
				)}
				<Divider />
				<MenuItem onClick={onSelectAll}>
					<ListItemIcon>
						<SelectAllIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText>{selectedCount === jobCount ? "Deselect All" : "Select All"}</ListItemText>
				</MenuItem>
				{selectedCount > 0 && (
					<>
						<MenuItem onClick={handleDuplicateSelected}>
							<ListItemIcon>
								<DuplicateIcon fontSize="small" />
							</ListItemIcon>
							<ListItemText>Duplicate Selected ({selectedCount})</ListItemText>
						</MenuItem>
						{canMerge && (
							<MenuItem onClick={handleMergeSelected}>
								<ListItemIcon>
									<MergeIcon fontSize="small" />
								</ListItemIcon>
								<ListItemText>Merge Selected ({selectedCount})</ListItemText>
							</MenuItem>
						)}
						{canSplit && onSplitSelected && (
							<MenuItem onClick={handleSplitSelected}>
								<ListItemIcon>
									<SplitIcon fontSize="small" />
								</ListItemIcon>
								<ListItemText>Split by Features ({selectedFeatureIds?.size})</ListItemText>
							</MenuItem>
						)}
						<Divider />
						<MenuItem onClick={handleDeleteSelected}>
							<ListItemIcon>
								<DeleteIcon fontSize="small" color="error" />
							</ListItemIcon>
							<ListItemText primaryTypographyProps={{ color: "error" }}>Delete Selected ({selectedCount})</ListItemText>
						</MenuItem>
					</>
				)}
			</Menu>

			{/* Floating Action Button for Add Job */}
			<Fab
				color="primary"
				aria-label="add job"
				onClick={onAddJob}
				sx={{
					position: "fixed",
					bottom: 16,
					right: 16,
					zIndex: 1000,
				}}
			>
				<AddIcon />
			</Fab>

			{/* Hidden file input for CSV upload */}
			<input ref={fileInputRef} type="file" accept=".csv" hidden onChange={handleFileChange} />

			{/* CSV Import Confirmation Dialog */}
			<Dialog open={!!csvImportPending} onClose={onCancelCSVImport}>
				<DialogTitle>Apply CSV Changes?</DialogTitle>
				<DialogContent>
					{csvImportPending && (
						<>
							<Typography>This will:</Typography>
							<ul>
								{csvImportPending.summary.updated > 0 && (
									<li>Update {csvImportPending.summary.updated} existing job(s)</li>
								)}
								{csvImportPending.summary.added > 0 && <li>Add {csvImportPending.summary.added} new job(s)</li>}
								{csvImportPending.summary.removed > 0 && <li>Remove {csvImportPending.summary.removed} job(s)</li>}
							</ul>
							{csvImportPending.summary.changedColumns.length > 0 && (
								<>
									<Typography sx={{ mt: 1 }}>Columns with changes:</Typography>
									<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
										{csvImportPending.summary.changedColumns.join(", ")}
									</Typography>
								</>
							)}
							{csvImportPending.summary.warnings.length > 0 && (
								<Alert severity="warning" sx={{ mt: 1 }}>
									{csvImportPending.summary.warnings.length} warning(s). Check data after import.
								</Alert>
							)}
						</>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={onCancelCSVImport}>Cancel</Button>
					<Button onClick={onConfirmCSVImport} variant="contained">
						Apply
					</Button>
				</DialogActions>
			</Dialog>
			{/* CSV Import Error Dialog */}
			<Dialog open={!!csvImportErrors} onClose={onDismissCsvImportErrors}>
				<DialogTitle>CSV Import Failed</DialogTitle>
				<DialogContent>
					{csvImportErrors?.[0] && <Alert severity="error">{csvImportErrors[0]}</Alert>}
					{csvImportErrors?.slice(1).map((info, i) => (
						<Typography key={i} variant="body2" color="text.secondary" sx={{ mt: 1 }}>
							{info}
						</Typography>
					))}
				</DialogContent>
				<DialogActions>
					<Button onClick={onDismissCsvImportErrors} variant="contained">
						OK
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
